<template>
  <div>
    <q-list v-if="checklists && checklists.length > 0">
      <q-expansion-item
        v-for="checklist in checklists"
        :key="checklist.id"
        :default-opened="!isChecklistComplete(checklist)"
        expand-icon="mdi-chevron-down"
        header-class="q-py-md checklist-header"
        class="checklist-group"
      >
        <template #header>
          <q-item-section>
            <q-item-label class="text-subtitle1 text-weight-medium text-blue-grey-9">
              {{ checklist.template ? checklist.template.name : 'General' }}
              <span
                v-if="checklist.template_version"
                class="text-caption text-grey-6 q-ml-xs"
              >v{{ checklist.template_version }}</span>
            </q-item-label>
            <q-item-label caption>
              {{ completedCount(checklist) }} of {{ checklist.items.length }} completed
            </q-item-label>
          </q-item-section>
          <q-item-section
            v-if="checklist.update_available && checklist.latest_template"
            side
            @click.stop
          >
            <q-btn
              dense
              no-caps
              unelevated
              color="orange-7"
              icon="mdi-update"
              :label="`Update to v${checklist.latest_template.version}`"
              :loading="updatingChecklistId === checklist.id"
              @click.stop="confirmUpdateVersion(checklist)"
            >
              <q-tooltip>A newer version of this template is available</q-tooltip>
            </q-btn>
          </q-item-section>
        </template>

        <div class="q-px-md q-pt-sm q-pb-md q-gutter-y-sm checklist-items">
          <ChecklistItemCard
            v-for="item in sortedItems(checklist.items)"
            :key="item.id"
            :item="item"
            :attendees="attendees"
            :toggling="togglingItemId === item.id"
            :invoice-status="invoiceStatus"
            :quotation-status="quotationStatus"
            :form-statuses="formStatuses"
            :allow-delete="canDeleteItem(checklist, item)"
            :from-required-form="isFromRequiredForm(item)"
            @toggle="toggleItem(checklist, item)"
            @assign="(userId) => assignItem(item, userId)"
            @open-file-dialog="openFileDialog(item)"
            @uploaded="(files) => onUploadedFiles(item, files)"
            @delete-photo="(attachment) => deletePhoto(item, attachment)"
            @view-file="viewingFileID = $event"
            @invoice-action="$emit('invoiceAction', $event)"
            @quotation-action="$emit('quotationAction', $event)"
            @form-action="$emit('formAction', $event)"
            @delete="deleteItem(item)"
          />
        </div>
      </q-expansion-item>
    </q-list>

    <div
      v-if="!checklists || checklists.length === 0"
      class="text-grey-6 q-px-md q-pt-md"
    >
      No tasks yet
    </div>

    <div class="q-px-md q-py-md">
      <q-btn
        outline
        no-caps
        color="primary"
        icon="mdi-plus"
        label="Add task"
        :loading="addingTask"
        @click="$refs.addTaskDialog.open()"
      />
    </div>

    <file-select-dialog
      ref="fileSelectDialog"
      :selected="fileDialogSelected"
      :maximum="0"
      :preview-mode="false"
      restrict-to-type="IMAGE"
      @saved="onFileDialogSaved"
    />

    <ViewFileDialog
      v-if="viewingFileID"
      :id="viewingFileID"
      @close="viewingFileID = null"
    />

    <TaskStepperDialog
      ref="addTaskDialog"
      mode="appointment"
      :loading="addingTask"
      :used-form-ids="usedFormIds"
      @save="onAddTask"
    />
  </div>
</template>

<script>
import ViewFileDialog from 'components/app/files/partials/view/Dialog'
import ChecklistItemCard from './ChecklistItemCard'
import TaskStepperDialog from 'components/app/checklists/TaskStepperDialog.vue'

export default {
  components: {
    ViewFileDialog,
    ChecklistItemCard,
    TaskStepperDialog
  },
  props: {
    appointmentId: {
      type: Number,
      required: true
    },
    checklists: {
      type: Array,
      default: () => []
    },
    attendees: {
      type: Array,
      default: () => []
    },
    invoiceStatus: {
      type: Object,
      default: null
    },
    quotationStatus: {
      type: Object,
      default: null
    },
    formStatuses: {
      type: Object,
      default: null
    },
    requiredFormIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['updated', 'invoiceAction', 'quotationAction', 'formAction', 'refresh'],
  data () {
    return {
      togglingItemId: null,
      updatingChecklistId: null,
      addingTask: false,
      viewingFileID: null,
      activePhotoItem: null,
      fileDialogSelected: []
    }
  },
  computed: {
    // Form template ids already linked to an existing task on this appointment,
    // used to stop the same form being added to a second task.
    usedFormIds () {
      const ids = []
      for (const checklist of this.checklists || []) {
        for (const item of checklist.items || []) {
          for (const formId of item.required_form_template_ids || []) {
            if (!ids.includes(formId)) ids.push(formId)
          }
        }
      }
      return ids
    }
  },
  methods: {
    // Ad-hoc tasks can be removed, unless they're tied to a form that is required
    // on the appointment itself — those must be unrequired on the appointment first.
    canDeleteItem (checklist, item) {
      if (checklist.checklist_template_id) return false
      return !this.isFromRequiredForm(item)
    },
    // Tasks auto-created because their template is required on the appointment
    isFromRequiredForm (item) {
      const itemForms = item.required_form_template_ids || []
      return itemForms.some(id => this.requiredFormIds.includes(id))
    },
    async onAddTask (payload) {
      this.addingTask = true
      try {
        const { status } = await this.$post(
          `v1/appointments/${this.appointmentId}/checklists/items`,
          payload
        )
        if (status === 'success') {
          this.$refs.addTaskDialog.close()
          this.$emit('refresh')
        }
      }
      finally {
        this.addingTask = false
      }
    },
    async deleteItem (item) {
      const { status } = await this.$remove(
        `v1/appointments/${this.appointmentId}/checklists/items/${item.id}`
      )
      if (status === 'success') {
        this.$emit('refresh')
      }
    },
    sortedItems (items) {
      if (!items) return []
      return [...items].sort((a, b) => a.sort_order - b.sort_order)
    },
    isChecklistComplete (checklist) {
      return checklist.items && checklist.items.every(i => i.is_completed)
    },
    completedCount (checklist) {
      if (!checklist.items) return 0
      return checklist.items.filter(i => i.is_completed).length
    },
    getFiles (item) {
      if (!item.attachments) return []
      return item.attachments.map(a => a.file).filter(Boolean)
    },
    openFileDialog (item) {
      this.activePhotoItem = item
      this.fileDialogSelected = this.getFiles(item)
      this.$nextTick(() => {
        this.$refs.fileSelectDialog.open()
      })
    },
    async onFileDialogSaved (files) {
      const item = this.activePhotoItem
      if (!item) return

      const existingFileIds = this.getFiles(item).map(f => f.id)
      const newFileIds = files.map(f => f.id)

      const toAdd = newFileIds.filter(id => !existingFileIds.includes(id))
      const toRemove = item.attachments
        ? item.attachments.filter(a => a.file && !newFileIds.includes(a.file.id))
        : []

      let lastResult = null

      if (toAdd.length > 0) {
        lastResult = await this.$post(
          `v1/appointments/${this.appointmentId}/checklists/${item.id}/photos`,
          { file_ids: toAdd }
        )
      }

      for (const attachment of toRemove) {
        lastResult = await this.$remove(
          `v1/appointments/${this.appointmentId}/checklists/${item.id}/photos/${attachment.id}`
        )
      }

      if (lastResult && lastResult.status === 'success') {
        this.updateItemInPlace(item, lastResult.payload.item)
      }

      this.activePhotoItem = null
    },
    async onUploadedFiles (item, files) {
      const fileIds = files.map(f => f.id)

      const { status, payload } = await this.$post(
        `v1/appointments/${this.appointmentId}/checklists/${item.id}/photos`,
        { file_ids: fileIds }
      )

      if (status === 'success') {
        this.updateItemInPlace(item, payload.item)
      }
    },
    async deletePhoto (item, attachment) {
      const { status, payload } = await this.$remove(
        `v1/appointments/${this.appointmentId}/checklists/${item.id}/photos/${attachment.id}`
      )

      if (status === 'success') {
        this.updateItemInPlace(item, payload.item)
      }
    },
    async toggleItem (checklist, item) {
      if (this.togglingItemId) return

      this.togglingItemId = item.id

      try {
        const { status, payload } = await this.$put(
          `v1/appointments/${this.appointmentId}/checklists/${item.id}/toggle`
        )

        if (status === 'success') {
          this.updateItemFromPayload(checklist, item, payload.item)
          this.$emit('updated', payload)
        }
      }
      finally {
        this.togglingItemId = null
      }
    },
    confirmUpdateVersion (checklist) {
      const name = checklist.template ? checklist.template.name : 'this checklist'
      this.$q.dialog({
        title: 'Update checklist',
        message: `Update "${name}" to v${checklist.latest_template.version}? Completed items that still exist will stay ticked; items removed from the template will be dropped.`,
        cancel: true,
        persistent: true,
        ok: { label: 'Update', color: 'orange-7', unelevated: true }
      }).onOk(() => this.updateChecklistVersion(checklist))
    },
    async updateChecklistVersion (checklist) {
      if (this.updatingChecklistId) return

      this.updatingChecklistId = checklist.id

      try {
        const { status, payload } = await this.$put(
          `v1/appointments/${this.appointmentId}/checklists/${checklist.id}/update-version`
        )

        if (status === 'success') {
          // Replace the checklist's items + version metadata in place.
          checklist.items = payload.checklist.items
          checklist.template_version = payload.checklist.template_version
          checklist.update_available = payload.checklist.update_available
          checklist.latest_template = payload.checklist.latest_template
          checklist.checklist_template_id = payload.checklist.checklist_template_id
          if (payload.checklist.template) {
            checklist.template = payload.checklist.template
          }
          this.$emit('updated', payload)
        }
      }
      finally {
        this.updatingChecklistId = null
      }
    },
    async assignItem (item, userId) {
      const { status, payload } = await this.$put(
        `v1/appointments/${this.appointmentId}/checklists/${item.id}/assign`,
        { assigned_to: userId }
      )

      if (status === 'success') {
        this.updateItemInPlace(item, payload.item)
      }
    },
    updateItemInPlace (item, updatedItem) {
      item.attachments = updatedItem.attachments
      item.is_completed = updatedItem.is_completed
      item.completed_by = updatedItem.completed_by
      item.completed_at = updatedItem.completed_at
      item.completed_by_user = updatedItem.completed_by_user
      item.assigned_to = updatedItem.assigned_to
      item.assigned_to_user = updatedItem.assigned_to_user
    },
    updateItemFromPayload (checklist, item, updatedItem) {
      const idx = checklist.items.findIndex(i => i.id === item.id)
      if (idx !== -1) {
        checklist.items.splice(idx, 1, { ...checklist.items[idx], ...updatedItem })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.checklist-group {
  border-radius: 8px;
  margin: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  :deep(.checklist-header) {
    background: rgba(0, 0, 0, 0.02);
  }

  .checklist-items {
    background: rgba(0, 0, 0, 0.01);
  }
}

// Mobile: tighten the accordion's outer margin and inner content padding so the
// task cards get more width instead of sitting in a deeply inset box.
@media (max-width: 599.98px) {
  .checklist-group {
    margin: 8px 8px;

    .checklist-items {
      padding: 8px;
    }
  }
}
</style>
